/**
 * useTaskSummaryGeneration Hook
 *
 * Handles streaming task summary generation with proper error handling and state management.
 * Encapsulates all API communication and streaming logic.
 */

import { useCallback, useEffect, useRef } from "react";
import type { TaskSummaryData, TaskSummaryRequest } from "@/lib/types/ai";
import { parseStreamEvent } from "@/lib/utils/ai/stream-parser";

export interface UseTaskSummaryGenerationOptions {
  onContentUpdate?: (content: string) => void;
  onComplete?: (summary: TaskSummaryData) => void;
  onError?: (error: string) => void;
}

/**
 * Hook to generate task summaries with streaming support
 */
export function useTaskSummaryGeneration(options?: UseTaskSummaryGenerationOptions) {
  /**
   * Controllers for the streams still running.
   *
   * The read loop below had no signal and no cancel, so a component that
   * unmounted mid-generation kept pulling chunks until the model finished,
   * calling `onContentUpdate` (and finally `onComplete`) into a closure that
   * still held the unmounted tree. A Set because one host can start a second
   * generation before the first is done.
   */
  const abortControllersRef = useRef<Set<AbortController>>(new Set());

  // Cancel every open stream when the hook's owner unmounts.
  useEffect(() => {
    const controllers = abortControllersRef.current;
    return () => {
      for (const controller of controllers) controller.abort();
      controllers.clear();
    };
  }, []);

  const generateSummary = useCallback(
    async (
      taskData: TaskSummaryRequest,
      cacheKey: string,
      onStreamingStart?: () => void,
      onStreamingEnd?: () => void
    ): Promise<TaskSummaryData> => {
      const controller = new AbortController();
      abortControllersRef.current.add(controller);

      const startTime = Date.now();
      let content = "";
      const buildSummary = (): TaskSummaryData => ({
        content,
        cacheKey,
        taskId: taskData.taskId,
        createdAt: new Date(),
        metadata: {
          noteCount: taskData.taskNotes.length,
          processingTime: Date.now() - startTime,
        },
      });

      try {
        const response = await fetch("/api/ai/task-summary", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(taskData),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to generate summary: ${response.statusText}`);
        }

        if (!response.body) {
          throw new Error("No response body");
        }

        onStreamingStart?.();

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split("\n");

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const data = line.slice(6);
              if (data === "[DONE]") {
                onStreamingEnd?.();
                break;
              }

              try {
                const parsed = parseStreamEvent(data);
                if (parsed?.content) {
                  content += parsed.content;
                  options?.onContentUpdate?.(content);
                }
              } catch (e) {
                // Ignore parsing errors for incomplete chunks
              }
            }
          }
        }

        const summary = buildSummary();

        options?.onComplete?.(summary);
        return summary;
      } catch (err) {
        // An unmount is not a failure: hand back what streamed and fire
        // neither callback, so the abort never reaches the user as an error
        // and `onComplete` never touches a tree that is gone.
        if (controller.signal.aborted) return buildSummary();
        const errorMessage = err instanceof Error ? err.message : "Failed to generate summary";
        options?.onError?.(errorMessage);
        throw err;
      } finally {
        abortControllersRef.current.delete(controller);
      }
    },
    [options]
  );

  return { generateSummary };
}
